import { CK_ENV } from 'ck-core/source/data/ckEnv';

import _ from 'lodash';
import React from 'react';

import './index.less';

import classnames from 'classnames';
import { PaymentButton, UPDATE_DETAILS } from '../PaymentButton';
import { lang as PaymentFormLang } from '../PaymentForm/lang';
import Errors from 'ck-core/source/services/Errors';
import Language from 'ck-core/source/services/Language';
import Form from 'ck-core-client/source/ui/Form';
import { SUBMIT_STATE } from 'ck-core-client/source/ui/Form';
import EmailInput from 'ck-core-client/source/ui/form/EmailInput';
import TextInput from 'ck-core-client/source/ui/form/TextInput';
import { lang } from './lang';
import { randomString } from 'ck-core/source/functions/randomString';
import { MonetaryValue } from 'ck-core/source/classes/MonetaryValue';
import { withAppContext, AppContext } from 'ck-core-client/source/context';
import { FACEBOOK_CUSTOM_EVENT } from 'ck-core-client/source/trackers/Facebook';
import { ADWORDS_CONVERSION_LABEL } from 'ck-core-client/source/trackers/Adwords';

import checkoutSecureIcon from 'ck-core-client/graphics/checkout/checkout-secure-icon.svg';
import creditCards from 'ck-core-web/graphics/generic/credit-cards.png';

import { PaymentMethodProps } from '../PaymentForm';
import { STRIPE_STATUS } from 'ck-core/source/data/StripeStatus';
import { injectStripe, CardElement } from 'react-stripe-elements';
import { completeSca } from 'ck-website/source/functions/completeSca';

interface OwnProps {
	paymentSubmitting?: boolean;
	errorMessage?: string;
	validCardExpected: boolean;
	appContext: AppContext;
	/** Handle success after negotiating an SCA payment */
	handleSuccess: (request: any, result: any) => Promise<any>;

	stripe: any;
}

export type Props = OwnProps & PaymentMethodProps;

interface State {
	errorMessage?: string;
	scaGathering: boolean;
	/** Whether the stripe element is reporting that valid CC details have been input */
	ccValid: boolean;
	validCardExpected: boolean;
}

export class CreditCardForm extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);

		this.allowTestAutofill = CK.config.environment.name !== CK_ENV.PRODUCTION;

		this.state = {
			scaGathering: false,
			ccValid: false,
			validCardExpected: props.validCardExpected
		};

		const { trackers } = props.appContext;
		const { trackFacebookPixel, trackAdwordsConversion } = trackers;

		trackFacebookPixel(FACEBOOK_CUSTOM_EVENT.VIEW_CARD_FORM);
		trackAdwordsConversion(ADWORDS_CONVERSION_LABEL.VIEW_CARD_FORM);
	}

	testAutofill = event => {
		if (event.ctrlKey && event.keyCode === 67) {
			this.form.fillValues({
				name: 'Fortismere Bear',
				email: 'fortismere+localCard' + randomString() + '@codekingdoms.com'
			});
		}
	};

	componentDidMount() {
		if (this.allowTestAutofill && typeof window !== 'undefined') {
			window.addEventListener('keydown', this.testAutofill);
		}
	}

	componentWillUnmount() {
		if (this.allowTestAutofill && typeof window !== 'undefined') {
			window.removeEventListener('keydown', this.testAutofill);
		}
	}

	handleSubmit = async data => {
		const { validCardExpected } = this.state;
		this.setState({ errorMessage: undefined });
		const isFreeUser = this.props.coupon && this.props.coupon.percentOff() === 100;

		try {
			this.props.setPaymentSubmitting(true);

			if (isFreeUser) {
				return this.props.handleSubmit(data);
			}

			const email = validCardExpected ? this.props.email : data.email;

			if (this.props.isUpdateDetails) {
				const { clientSecret } = await CK.clientSockets.users.emit('getStripeSetupIntent');

				const { setupIntent, error } = await this.props.stripe.handleCardSetup(clientSecret, {
					payment_method_data: {
						billing_details: {
							name: data.name,
							email: email
						}
					}
				});

				if (error) {
					throw error;
				}

				await this.props.handleSubmit({
					stripeResponse: {
						id: setupIntent.payment_method
					}
				});
			} else {
				this.props.trackAnalytic('CardPaymentAttempt', {
					email
				});

				let stripeResponse;

				if (!validCardExpected) {
					stripeResponse = await this._createPaymentMethod(data);
				}

				const { request, result } = await this.props.handleSubmit(
					{
						stripeResponse,
						email
					},
					true
				);

				if (result.status === STRIPE_STATUS.INCOMPLETE) {
					await this.handleScaPayment(result.purchaseId, result.paymentIntentClientSecret);
				}

				await this.props.handleSuccess(request, result);
			}
		} catch (err) {
			if (validCardExpected) {
				this.setState({
					validCardExpected: false,
					errorMessage: lang.SAVED_PAYMENT_FAILED
				});
			}

			if (!(err instanceof Errors.StripeCreateTokenFailed) && !err.soft) {
				CK.logger.critical(err);
			}

			if (_.includes(err.message, 'Poll timed out')) {
				err.message = 'Sorry, the connection timed out, please try again.';
			}

			throw err;
		} finally {
			this.props.setPaymentSubmitting(false);
		}
	};

	async _createPaymentMethod(data: any) {
		const { paymentMethod, error } = await this.props.stripe.createPaymentMethod('card', {
			billing_details: {
				name: data.name,
				email: data.email
			}
		});

		if (error) {
			const err = new Errors.StripeCreateTokenFailed({
				message: Language.mapVariables(PaymentFormLang.FAILED.MESSAGE, {
					message: error.message,
					prepaidCardMessage: ''
				}),
				tags: error
			});

			this.props.trackAnalytic('StripeCreateTokenFailed', {
				message: error.message,
				errorCode: error.code,
				errorType: error.type
			});

			throw err;
		}

		return paymentMethod;
	}

	handleScaPayment = async (purchaseId: string, paymentIntentClientSecret: string) => {
		try {
			this.setState({
				scaGathering: true
			});

			this.props.setPaymentSubmitting(true);

			await completeSca(purchaseId, paymentIntentClientSecret);
		} finally {
			this.setState({
				scaGathering: false
			});

			this.props.setPaymentSubmitting(false);
		}
	};

	render() {
		const {
			priceInCurrency,
			isUpdateDetails,
			paymentDate,
			isSubscriptionChange,
			stripe
		} = this.props;
		const { ccValid, validCardExpected } = this.state;

		if (!stripe) {
			return <div>Loading...</div>;
		}

		const initialState =
			this.state.errorMessage || this.props.errorMessage
				? SUBMIT_STATE.ERROR
				: SUBMIT_STATE.FILLING;

		const classes = classnames('CreditCardForm', this.state.scaGathering && 'sca');

		let scaCover;

		if (this.state.scaGathering) {
			const inner = <p>{lang.SCA_REQUIRED_MESSAGE}</p>;

			scaCover = (
				<div className="sca" id="sca">
					{inner}
				</div>
			);
		}

		const isFreeUser = this.props.coupon && this.props.coupon.percentOff() === 100;

		let form;

		if (validCardExpected) {
			const buttonProps = _.defaults(
				{
					handleClick: this.handleSubmit,
					pending: this.props.paymentSubmitting
				},
				this.props
			);

			form = (
				<>
					<p className="topMessage" style={{ textAlign: 'center' }}>
						{lang.VALID_CARD_EXPECTED}
						<a href="#" id="addNewCard" onClick={() => this.setState({ validCardExpected: false })}>
							{lang.ADD_NEW_CARD}
						</a>.
					</p>
					<PaymentButton
						{...buttonProps}
						priceOrUpdateDetails={
							isUpdateDetails
								? UPDATE_DETAILS
								: new MonetaryValue(priceInCurrency.price, priceInCurrency.currency)
						}
					/>
				</>
			);
		} else {
			form = (
				<Form
					handleSubmit={this.handleSubmit}
					initialState={initialState}
					errorMessage={this.state.errorMessage || this.props.errorMessage}
					customSubmitButton={(buttonProps: any) => {
						return (
							<PaymentButton
								priceOrUpdateDetails={
									isUpdateDetails
										? UPDATE_DETAILS
										: new MonetaryValue(priceInCurrency.price, priceInCurrency.currency)
								}
								paymentDate={paymentDate}
								{...buttonProps}
								disabled={(!isFreeUser && !ccValid) || buttonProps.disabled}
							/>
						);
					}}
					ref={(form: any) => (this.form = form)}
				>
					<EmailInput label="Cardholder email" name="email" value={this.props.email} />
					{!isFreeUser && <TextInput name="name" label="Cardholder Name" />}
					{!isFreeUser && (
						<CardElement
							style={{
								base: {
									fontSize: '14px',
									fontFamily: '"Zona Pro", Verdana'
								}
							}}
							onChange={(changeData: any) => {
								this.setState(prevState => {
									if (prevState.ccValid === changeData.complete) {
										return null;
									}

									return {
										ccValid: changeData.complete
									};
								});
							}}
						/>
					)}
				</Form>
			);
		}

		return (
			<div className={classes}>
				{scaCover}
				{form}
				{isSubscriptionChange && <p>{lang.UPGRADE}</p>}
				<div className="CreditCardForm__secureInfo">
					<div className="CreditCardForm__secureInfo-header">
						<img src={checkoutSecureIcon} className="CreditCardForm__secureIcon" />
						Secure
					</div>

					<div className="CreditCardForm__secureInfo-description">
						We use SSL (Secure Socket Layer) technology to encrypt the information you submit
						through our site.
					</div>
					<div className="CreditCardForm__secureInfo-cards">
						<img src={creditCards} />
					</div>
				</div>
			</div>
		);
	}
}

export const ConnectedCreditCardForm = withAppContext(injectStripe(CreditCardForm));
